import { BufferAttribute, BufferGeometry, ClampToEdgeWrapping, DataTexture, DataUtils, HalfFloatType, LinearFilter, Mesh, RedFormat, ShaderMaterial, Vector2 } from "three";
import { HEIGHT_SIDE, WORLD_HALF, WORLD_SIZE } from "@eldrun/worldgen";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { assemble } from "./chunks.ts";
import { SURF_PROFILE_MEAN, surfUniforms } from "./surf.ts";

/** Textura R16F do heightfield: profundidade analítica do mar sem depth texture (doc 05). */
export function createHeightTexture(heights: Float32Array): DataTexture {
  const half = new Uint16Array(heights.length);
  for (let i = 0; i < heights.length; i++) half[i] = DataUtils.toHalfFloat(heights[i] as number);
  const texture = new DataTexture(half, HEIGHT_SIDE, HEIGHT_SIDE, RedFormat, HalfFloatType);
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

/** Meia-largura do mar. */
const SEA_HALF = 2500;
/**
 * Anéis da malha em volta da câmera (CDLOD): passo da célula e meia-largura (m). Cada anel tem o dobro
 * do passo do de dentro; o último chega a ±250 m, além de onde as ondas se apagam (240 m).
 */
export const SEA_LEVELS: readonly { cell: number; half: number }[] = [
  { cell: 1.25, half: 60 },
  { cell: 2.5, half: 120 },
  { cell: 5, half: 250 },
];
/** Passo com que a malha segue a câmera: o do anel mais grosso, múltiplo de todos (cada rede cai sobre si). */
export const SEA_SNAP = 5;
/** Segmentos da borda lisa, de ±250 m até `SEA_HALF` (passo de 5 m que cresce ao cubo). */
const SEA_RIM = 16;

/**
 * Faixa (distância de Chebyshev à câmera) em que os vértices do anel se fundem na rede do anel de fora:
 * termina 8 m antes da borda — o passo da malha (5 m) mais a folga de meio passo da câmera —, para o
 * vértice que sai ou entra no anel num passo já estar sobre a rede grossa. 0 no anel mais grosso.
 */
export function seaMorphRange(level: number): [number, number] {
  if (level >= SEA_LEVELS.length - 1) return [0, 0];
  const half = SEA_LEVELS[level]!.half;
  const end = half - SEA_SNAP - SEA_SNAP / 2 - 0.5;
  return [end - half * 0.3, end];
}

/**
 * Espelho em TS da fusão do vértice do shader (testes): posição no mundo, célula e faixa (`aMorph`) e a
 * câmera → onde o vértice fica.
 */
export function seaMorphed(x: number, z: number, morph: readonly [number, number, number], cameraX: number, cameraZ: number): [number, number] {
  const [cell, start, end] = morph;
  if (end <= 0) return [x, z];
  const chebyshev = Math.max(Math.abs(x - cameraX), Math.abs(z - cameraZ));
  const t = Math.min(1, Math.max(0, (chebyshev - start) / (end - start)));
  const m = t * t * (3 - 2 * t);
  const odd = (v: number) => {
    const f = v / (2 * cell) - Math.floor(v / (2 * cell));
    return f >= 0.25 ? 1 : 0;
  };
  return [x - odd(x) * cell * m, z - odd(z) * cell * m];
}

/** Coordenadas da borda lisa (m, só o lado positivo, de 250 m a `SEA_HALF`). */
function rimCoordinates(): number[] {
  const inner = SEA_LEVELS[SEA_LEVELS.length - 1]!.half;
  const growth = (SEA_HALF - inner - SEA_RIM * SEA_SNAP) / SEA_RIM ** 3;
  return Array.from({ length: SEA_RIM }, (_, k) => inner + (k + 1) * SEA_SNAP + growth * (k + 1) ** 3);
}

/**
 * Malha do mar em anéis (CDLOD): 1,25 m até ±60 m, 2,5 m até ±120 m, 5 m até ±250 m e uma borda lisa
 * até ±2,5 km — ~31 mil vértices (a grade uniforme de antes tinha 201 mil). O atributo `aMorph` diz a
 * célula do vértice e a faixa da fusão (x: célula, yz: início e fim; z = 0 sem fusão): no fim da faixa
 * os vértices ímpares caem sobre os pares e o anel vira, exato, a rede do anel de fora — sem fresta
 * nem salto na troca. Triângulos todos na mesma diagonal (a→c), a que a fusão preserva.
 */
export function createSeaGeometry(): BufferGeometry {
  const positions: number[] = [];
  const morph: number[] = [];
  const indices: number[] = [];
  const ids = new Map<string, number>();
  const vertex = (x: number, z: number, cell: number, range: readonly [number, number]): number => {
    const key = `${x},${z}`;
    const known = ids.get(key);
    if (known !== undefined) return known;
    const id = positions.length / 3;
    ids.set(key, id);
    positions.push(x, 0, z);
    morph.push(cell, range[0], range[1]);
    return id;
  };
  // Anel fino primeiro: o vértice da divisa fica com a célula do anel de dentro (é par nele, a fusão não o move).
  const quad = (x0: number, z0: number, x1: number, z1: number, cell: number, range: readonly [number, number]) => {
    const a = vertex(x0, z0, cell, range);
    const b = vertex(x1, z0, cell, range);
    const d = vertex(x0, z1, cell, range);
    const c = vertex(x1, z1, cell, range);
    // Anti-horário visto de cima (normal +y).
    indices.push(a, d, c, a, c, b);
  };
  SEA_LEVELS.forEach(({ cell, half }, level) => {
    const n = Math.round(half / cell);
    const hole = level > 0 ? Math.round(SEA_LEVELS[level - 1]!.half / cell) : 0;
    const range = seaMorphRange(level);
    for (let j = -n; j < n; j++) {
      for (let i = -n; i < n; i++) {
        if (i >= -hole && i < hole && j >= -hole && j < hole) continue;
        quad(i * cell, j * cell, (i + 1) * cell, (j + 1) * cell, cell, range);
      }
    }
  });
  // Borda lisa: grade de produto com o passo de 5 m ao longo do anel (sem junção em T) e o crescente para fora.
  const inner = SEA_LEVELS[SEA_LEVELS.length - 1]!.half;
  const rim = rimCoordinates();
  const along: number[] = [];
  for (let k = -inner / SEA_SNAP; k <= inner / SEA_SNAP; k++) along.push(k * SEA_SNAP);
  const axis = [...rim.map((r) => -r).reverse(), ...along, ...rim];
  const none = [0, 0] as const;
  for (let j = 0; j < axis.length - 1; j++) {
    for (let i = 0; i < axis.length - 1; i++) {
      const [x0, x1, z0, z1] = [axis[i]!, axis[i + 1]!, axis[j]!, axis[j + 1]!];
      if (x0 >= -inner && x1 <= inner && z0 >= -inner && z1 <= inner) continue;
      quad(x0, z0, x1, z1, 0, none);
    }
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(new Float32Array(positions), 3));
  geometry.setAttribute("aMorph", new BufferAttribute(new Float32Array(morph), 3));
  geometry.setIndex(new BufferAttribute(positions.length / 3 > 65535 ? new Uint32Array(indices) : new Uint16Array(indices), 1));
  return geometry;
}

const vertexShader = /* glsl */ `
${assemble("vertex", ["atmo", "surf"])}
uniform sampler2D uHeights;
uniform vec2 uWorldMin;
uniform float uWorldSize;
uniform float uHeightSide;
// Célula do vértice e a faixa da fusão com o anel de fora (createSeaGeometry).
in vec3 aMorph;
out vec3 vWorldPos;
out vec3 vNormal;
// A arrebentação (Fase 15): fase da onda, força da quebra (0 longe da praia) e o espraiado (0 fora da faixa da costa).
out float vPhase;
out float vBreak;
out float vSwash;

// Ondas de Gerstner pequenas: só visuais, a física usa mar plano (doc 05, risco 10). Um seno e um cosseno por onda.
vec3 gerstner(vec2 p, vec2 dir, float amplitude, float wavelength, float speed, inout vec3 tangent, inout vec3 binormal) {
  float k = 6.2831853 / wavelength;
  float f = k * (dot(dir, p) - speed * uTime);
  float s = sin(f);
  float c = cos(f);
  float ka = k * amplitude;
  float q = 0.5;
  tangent += vec3(-q * dir.x * dir.x * ka * s, dir.x * ka * c, -q * dir.x * dir.y * ka * s);
  binormal += vec3(-q * dir.x * dir.y * ka * s, dir.y * ka * c, -q * dir.y * dir.y * ka * s);
  return vec3(q * amplitude * dir.x * c, amplitude * s, q * amplitude * dir.y * c);
}

float terrainAt(vec2 xz) {
  vec2 uv = (xz - uWorldMin) / uWorldSize;
  // Fora do mapa, mar aberto e fundo.
  if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) return -40.0;
  // A grade tem N amostras sobre N−1 intervalos: amostra no centro do texel correspondente.
  return textureLod(uHeights, (uv * (uHeightSide - 1.0) + 0.5) / uHeightSide, 0.0).r;
}

void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  // Fusão com o anel de fora (CDLOD): pela distância de Chebyshev à câmera (contínua com ela), o vértice
  // ímpar escorrega até o par de baixo. A malha anda de 5 m em 5 m, então a paridade no mundo é a da malha.
  vec2 toEye = world.xz - cameraPosition.xz;
  float chebyshev = max(abs(toEye.x), abs(toEye.y));
  float morph = aMorph.z > 0.0 ? smoothstep(aMorph.y, aMorph.z, chebyshev) : 0.0;
  if (morph > 0.0) {
    // 0 ou 1 exatos: fundido, o vértice cai em cima do par (mesma posição, mesma onda: sem fresta).
    vec2 odd = step(0.25, fract(world.xz / (2.0 * aMorph.x)));
    world.xz -= odd * aMorph.x * morph;
  }
  vec2 xz = world.xz;
  vec3 tangent = vec3(1.0, 0.0, 0.0);
  vec3 binormal = vec3(0.0, 0.0, 1.0);
  vec3 offset = vec3(0.0);
  float swash = 0.0;
  vPhase = 0.0;
  vBreak = 0.0;
  vSwash = 0.0;
  // Ondas somem a 240 m da câmera: além disso (a borda e o fim do último anel) o mar é plano e o vértice
  // não lê textura nem calcula onda. Pela posição já fundida: o vértice fundido e o par têm a mesma onda.
  float distanceToEye = length(xz - cameraPosition.xz);
  float near = 1.0 - smoothstep(120.0, 240.0, distanceToEye);
  if (near > 0.0) {
    float depth = uSeaLevel - terrainAt(xz);
    vec3 field = surfShoreField(xz);
    float shore = field.x;
    // Cada onda se apaga antes do anel em que teria menos de ~4 vértices por comprimento.
    float mid = 1.0 - smoothstep(70.0, 110.0, distanceToEye);
    float fine = 1.0 - smoothstep(25.0, 45.0, distanceToEye);
    // O marulho do largo acalma no raso: perto da praia quem manda é a arrebentação.
    float calm = clamp(depth / 5.0, 0.0, 1.0);
    offset += gerstner(xz, vec2(0.957826, 0.287348), 0.26 * calm * near, 26.0, 2.6, tangent, binormal);
    offset += gerstner(xz, vec2(-0.371391, 0.928477), 0.12 * calm * mid, 11.0, 1.7, tangent, binormal);
    if (fine > 0.0) offset += gerstner(xz, vec2(0.658505, -0.752577), 0.05 * calm * fine, 4.5, 1.1, tangent, binormal);

    // Arrebentação: a crista corre para a praia pela distância à costa, cresce quando o fundo sobe e
    // desmancha no raso (vira espuma e espraiado). Inclinação pela derivada da fase (o gradiente da
    // distância vem pronto na textura da costa). Longe da costa, nada disso.
    float zone = (1.0 - smoothstep(35.0, 85.0, shore)) * near;
    float swashZone = smoothstep(-30.0, -18.0, shore) * (1.0 - smoothstep(6.0, 16.0, shore)) * near;
    if (zone > 0.0 || swashZone > 0.0) {
      float set = surfSet(xz);
      float shoal = mix(0.3, 1.0, 1.0 - smoothstep(1.8, 9.0, depth));
      float amp = uSurf.z * set * zone * shoal * smoothstep(0.05, 1.1, depth);
      float phase = surfPhase(xz, shore);
      float u = fract(phase);
      offset.y += amp * (surfProfile(u) - ${SURF_PROFILE_MEAN.toFixed(4)});
      vec2 grad = field.yz / uSurf.x;
      float slope = amp * surfProfileSlope(u);
      tangent.y += slope * grad.x;
      binormal.y += slope * grad.y;
      // Espraiado: perto da costa a água sobe a areia e volta. Na terra longe da praia o mar fica no nível
      // (embaixo do chão): as baixadas do interior não alagam.
      swash = surfRunup(xz, set) * swashZone;
      vPhase = phase;
      vBreak = set * zone * (1.0 - smoothstep(0.9, 3.2, depth)) * smoothstep(0.02, 0.35, depth + swash);
      vSwash = swashZone;
    }
  }

  world.xz += offset.xz;
  world.y = uSeaLevel + offset.y + swash;
  vNormal = normalize(cross(binormal, tangent));
  vWorldPos = world.xyz;
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const fragmentShader = /* glsl */ `
${assemble("fragment", ["atmo", "fogGlow", "noise"])}
uniform sampler2D uHeights;
uniform vec2 uWorldMin;
uniform float uWorldSize;
uniform float uHeightSide;
in vec3 vWorldPos;
in vec3 vNormal;
in float vPhase;
in float vBreak;
in float vSwash;

void main() {
  // A lâmina d'água pelo chão sob o fragmento (a mesma textura do vértice): rasa no espraiado, funda no largo.
  vec2 uv = (vWorldPos.xz - uWorldMin) / uWorldSize;
  float ground = uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0 ? -40.0 : texture(uHeights, (uv * (uHeightSide - 1.0) + 0.5) / uHeightSide).r;
  float film = vWorldPos.y - ground;
  // Embaixo da areia: o chão já cobre (e a borda não briga com ele no depth).
  if (film <= 0.0) discard;

  // Do raso ao fundo só no valor (luminância linear); o matiz é o do ambiente agora: a névoa e o
  // horizonte (cinza de dia, creme no entardecer, azul à noite), com um resto de verde-azulado para ler
  // como água. Assim o mar acompanha o mundo cinza da referência em vez de um turquesa fixo.
  float value = mix(0.33, 0.115, smoothstep(0.0, 3.0, film));
  value = mix(value, 0.03, smoothstep(3.0, 20.0, film));
  vec3 water = seaHue() * value;

  vec3 normal = normalize(vNormal);
  vec3 view = normalize(cameraPosition - vWorldPos);
  float fresnel = pow(1.0 - clamp(dot(normal, view), 0.0, 1.0), 4.0);
  // Uniforms de THREE.Color já estão em espaço linear; só as constantes acima são sRGB.
  vec3 sky = mix(uHorizon, uZenith, 0.35);
  vec3 color = mix(water, sky, fresnel * 0.55);
  float ndl = clamp(dot(normal, uLightDir), 0.0, 1.0);
  // À noite a água escurece até um azul-petróleo (doc 05, Ciclo dia/noite): só a lua brilha nela.
  float dark = 1.0 - 0.82 * uNight;
  color *= mix(uAmbientSky * 0.6, vec3(1.0), 0.45 + 0.55 * ndl) * dark;
  // Brilho do sol: o reflexo largo e o ponto forte (contido, para o bloom não estourar o mar).
  vec3 halfway = normalize(uLightDir + view);
  float nh = max(dot(normal, halfway), 0.0);
  color += uLightColor * (pow(nh, 220.0) * 1.1 + pow(nh, 28.0) * 0.08);

  // Espuma: renda de ruído em duas escalas, rolando com o tempo. Só onde há quebra ou espraiado (o
  // vértice zera os dois longe da costa): o mar aberto, a maior parte da tela, não paga o ruído.
  float foam = 0.0;
  if (vBreak > 0.0 || vSwash > 0.0) {
    vec2 p = vWorldPos.xz;
    float lace = noise(p * 0.9 + vec2(uTime * 0.25, -uTime * 0.18)) * 0.6 + noise(p * 2.7 - vec2(uTime * 0.4, uTime * 0.1)) * 0.4;
    float u = fract(vPhase);
    // A crista que quebra (a frente, perto da fração 1) e o rastro de espuma atrás dela.
    float breaker = smoothstep(0.84, 0.97, u) * (1.0 - smoothstep(0.985, 1.0, u)) + exp(-u * 6.0) * 0.75;
    foam = smoothstep(1.0 - breaker * vBreak, 1.15 - breaker * vBreak, lace);
    // O espraiado: a linha branca na borda que sobe e desce a areia, e a renda na lâmina fina.
    float edge = (1.0 - smoothstep(0.0, 0.05, film)) * vSwash;
    float sheet = (1.0 - smoothstep(0.02, 0.16, film)) * vSwash;
    foam = clamp(max(foam, max(edge * 0.95, smoothstep(0.55, 0.75, lace) * sheet * 0.7)), 0.0, 1.0);
    vec3 foamColor = vec3(0.93, 0.95, 0.93) * mix(uAmbientSky * 0.5 + 0.55, vec3(1.0), ndl) * mix(1.0, 0.3, uNight);
    color = mix(color, foamColor, foam);
  }

  color = applyFog(color, vWorldPos, cameraPosition);
  // A lâmina rasa deixa ver a areia molhada; a espuma é opaca.
  float alpha = max(mix(0.38, 0.95, smoothstep(0.0, 2.5, film)), foam * 0.95);
  gl_FragColor = vec4(color, alpha);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export interface Sea {
  mesh: Mesh;
  /** Acompanha a câmera em passos fixos. */
  follow(x: number, z: number): void;
}

export function createSea(atmosphere: AtmosphereUniforms, heightTexture: DataTexture, shoreTexture: DataTexture): Sea {
  const geometry = createSeaGeometry();
  const material = new ShaderMaterial({
    name: "ilha/mar",
    uniforms: {
      ...atmosphere,
      uHeights: { value: heightTexture },
      uWorldMin: { value: new Vector2(-WORLD_HALF, -WORLD_HALF) },
      uWorldSize: { value: WORLD_SIZE },
      uHeightSide: { value: HEIGHT_SIDE },
      ...surfUniforms(shoreTexture),
    },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
  });
  const mesh = new Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.renderOrder = 10;
  return {
    mesh,
    follow(x, z) {
      mesh.position.set(Math.round(x / SEA_SNAP) * SEA_SNAP, 0, Math.round(z / SEA_SNAP) * SEA_SNAP);
    },
  };
}
